import { useState } from "react";
import type { InfoDialogSection } from "../../molecules/InfoDialog/InfoDialog.types";
import type { ListConfirmDialogProps } from "../../molecules/ListConfirmDialog/ListConfirmDialog.types";
import { useModelChanges, useModelReviewActions } from "../../../hooks/useModelReview";
import { getApiErrorMessage } from "../../../services/apiErrors";
import { modelReviewCopy as copy } from "../../../services/modelling/modelStatus.copy";
import { toModelReview } from "../../../services/modelling/modelStatusMappers";

export interface ModelReviewDialogView {
  isOpen: boolean;
  sections: InfoDialogSection[];
  /** Approve and Reject wait for the changes and for any action under way. */
  actionsDisabled: boolean;
  approving: boolean;
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
  confirm: ListConfirmDialogProps;
}

/** The review dialog's state: the changes, the action under way, and the Reject confirm. */
export const useModelReviewDialogState = (runId: string | null, onClose: () => void): ModelReviewDialogView => {
  const [confirming, setConfirming] = useState(false);
  const changes = useModelChanges(runId);
  const { approve, reject } = useModelReviewActions();
  const review = changes.data ? toModelReview(changes.data) : null;

  const close = () => {
    setConfirming(false);
    approve.reset();
    reject.reset();
    onClose();
  };
  const failure = approve.isError
    ? getApiErrorMessage(approve.error, copy.approveFailed)
    : reject.isError
      ? getApiErrorMessage(reject.error, copy.rejectFailed)
      : null;
  const sections: InfoDialogSection[] = changes.isError
    ? [{ id: "unreadable", heading: copy.failedHeading, text: getApiErrorMessage(changes.error, copy.unreadable) }]
    : review
      ? review.sections
      : [{ id: "loading", heading: copy.aboutHeading, text: copy.loading }];

  return {
    isOpen: Boolean(runId) && !confirming,
    sections: failure ? [{ id: "failed", heading: copy.failedHeading, text: failure }, ...sections] : sections,
    actionsDisabled: !review || approve.isPending || reject.isPending,
    approving: approve.isPending,
    onApprove: () => runId && approve.mutate(runId, { onSuccess: close }),
    onReject: () => {
      reject.reset();
      setConfirming(true);
    },
    onClose: close,
    confirm: {
      open: Boolean(runId) && confirming,
      title: copy.rejectTitle,
      message: review?.stopsAnswering.length ? copy.rejectMessageRetired : copy.rejectMessage,
      items: review?.stopsAnswering ?? [],
      moreLabel: copy.more,
      confirmLabel: copy.rejectConfirm,
      cancelLabel: copy.keepReviewing,
      closeLabel: copy.close,
      tone: "danger",
      onConfirm: () => {
        if (!runId || reject.isPending) return;
        reject.mutate(runId, { onSuccess: close, onError: () => setConfirming(false) });
      },
      onCancel: () => setConfirming(false),
    },
  };
};
